/**
 * MobileFeed.jsx — COMPLETE PRODUCTION v8.0
 * Place at: src/mobile/screens/MobileFeed.jsx
 *
 * ✅ EVERYTHING FROM DESKTOP — zero features missing:
 *
 *   FEED & POSTS
 *   • All 4 feed tabs (All / Following / Mine / Saved)
 *   • Sort chips (Hot / New / Top)
 *   • Tag filter strip with color accents
 *   • Search with debounce
 *   • Staggered post cascade animation
 *   • PostCard with full three-dots menu:
 *       Copy link · Follow/Unfollow · Report · Delete · Mod actions
 *   • Double-tap to like with heart burst
 *   • Optimistic vote / bookmark / report (FeedContext)
 *   • Paginated "Load older posts"
 *   • New-posts refresh banner
 *
 *   SPACES
 *   • Featured spaces horizontal strip at top of feed
 *   • Space cards woven into feed at positions 3 & 8
 *   • 🏡 Spaces browser sheet — search, join, view all spaces
 *   • SpaceFeed overlay opens on tap (join/view)
 *   • + Create Space button (eligible users: 50+ followers or admin)
 *   • CreateSpaceModal
 *
 *   USER
 *   • Mini user stats bar: Posts · Followers · Following
 *   • UserProfilePanel on tap of username/avatar
 *   • Compose FAB always pinned — never scrolls away
 *   • ✏️ compose shortcut in header
 *   • Banned notice, muted handling
 *
 *   PERFORMANCE
 *   • RAF-throttled scroll (no thrashing)
 *   • useMemo post list (stable ref for removePost)
 *   • Feed-level 5-minute cache (FeedContext)
 *   • AbortController on fetch
 *
 * IMPORT PATH:
 *   SpaceFeed  → ../../pages/SpaceFeed
 *   Everything else stays at ../../components/* or ../../hooks/*
 */

import React, {
  useState, useRef, useCallback, useMemo, useEffect, memo,
} from "react";
import { useFeedUser }                   from "../../hooks/useFeedUser";
import { FeedProvider, useFeedContext }   from "../../context/FeedContext";
import PostCard                           from "../../components/PostCard";
import PostComposer                       from "../../components/PostComposer";
import SpaceFeed                          from "../../pages/SpaceFeed";
import CreateSpaceModal                   from "../../components/CreateSpaceModal";
import UserProfilePanel                   from "../../components/UserProfilePanel";
import { joinSpace, searchSpaces }        from "../../lib/spaceQueries";
import { useTopSpaces }                   from "../../hooks/useTopSpaces";
import { TAGS, SORT_OPTIONS }             from "../../utils/moderation";

/* ─── Tab / sort config ──────────────────────────────────────────────────── */
const FEED_TABS = [
  { key: "all",       label: "All",      icon: "🌍" },
  { key: "following", label: "Following", icon: "👥" },
  { key: "mine",      label: "Mine",      icon: "✍️" },
  { key: "bookmarks", label: "Saved",     icon: "🔖" },
];
const SORT_META = {
  hot: { icon: "🔥", label: "Hot"  },
  new: { icon: "✨", label: "New"  },
  top: { icon: "⭐", label: "Top"  },
};
const EMPTY = {
  following: { icon: "👥", title: "Follow people",       sub: "Their posts appear here."           },
  bookmarks: { icon: "🔖", title: "Nothing saved yet",   sub: "Bookmark posts you love."           },
  mine:      { icon: "✍️", title: "Your canvas awaits",  sub: "Share your first thought."          },
  search:    { icon: "🔍", title: "No results",           sub: "Try different keywords."            },
  default:   { icon: "💬", title: "So quiet here…",      sub: "Be the first to start a chat."     },
};

/* ─── Haptic ─────────────────────────────────────────────────────────────── */
const haptic = (t = "light") => {
  try { navigator?.vibrate?.(t === "heavy" ? [28, 18, 28] : 12); } catch (_) {}
};

/* ═══════════════════════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════════════════════ */
const CSS = `
@keyframes mf8Up    { from{opacity:0;transform:translateY(18px) scale(.98)} to{opacity:1;transform:none} }
@keyframes mf8In    { from{opacity:0} to{opacity:1} }
@keyframes mf8Sheet { from{transform:translateY(102%)} to{transform:translateY(0)} }
@keyframes mf8Bop   { from{opacity:0;transform:scale(.88) translateY(-6px)} to{opacity:1;transform:none} }
@keyframes mf8Float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-9px)} }
@keyframes mf8Pulse { 0%,100%{opacity:1} 50%{opacity:.38} }
@keyframes mf8Spin  { to{transform:rotate(360deg)} }
@keyframes mf8FabIn { from{opacity:0;transform:scale(.7)} to{opacity:1;transform:scale(1)} }
@keyframes mf8SlideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }

/* hide scrollbars */
.mf8-sc::-webkit-scrollbar{display:none}
.mf8-sc{scrollbar-width:none;-webkit-overflow-scrolling:touch}

/* post card mobile tightening */
.mf8-posts .card{
  border-radius:20px!important;
  margin:0 0 10px!important;
  border:1px solid var(--sand-light,#EBE7DF)!important;
  box-shadow:0 1px 3px rgba(0,0,0,.04),0 4px 14px rgba(0,0,0,.05)!important;
  overflow:hidden!important;
  transition:transform .15s cubic-bezier(.25,1,.4,1)!important;
}
body.dark-theme .mf8-posts .card{
  border-color:rgba(255,255,255,.06)!important;
  background:var(--card,#1e2230)!important;
}

/* dark glass header */
body.dark-theme .mf8-hdr{
  background:rgba(16,19,28,.92)!important;
  border-bottom-color:rgba(255,255,255,.05)!important;
}
/* dark FAB */
body.dark-theme .mf8-fab{
  background:rgba(22,26,38,.95)!important;
  border-color:rgba(255,255,255,.08)!important;
  box-shadow:0 4px 20px rgba(0,0,0,.28),0 1px 4px rgba(0,0,0,.2)!important;
}
/* dark composer sheet */
body.dark-theme .mf8-sheet{
  background:var(--bg,#151820)!important;
}

/* pressable spring */
.mf8-btn{
  -webkit-tap-highlight-color:transparent;
  user-select:none;
  cursor:pointer;
  transition:transform .1s cubic-bezier(.25,1,.4,1);
}
.mf8-btn:active{transform:scale(.94)!important}
`;

function GlobalCSS() { return <style>{CSS}</style>; }

/* ═══════════════════════════════════════════════════════════════════════════
   MINI ATOMS
═══════════════════════════════════════════════════════════════════════════ */
const Chip = memo(({ active, color, children, onClick, style: sx }) => (
  <button className="mf8-btn" onClick={onClick} style={{
    flexShrink: 0,
    padding: "8px 15px", borderRadius: 99,
    border: `1.5px solid ${active && color ? color : active ? "var(--ink,#2D364D)" : "var(--sand-light,#EBE7DF)"}`,
    background: active && color ? `${color}18` : active ? "var(--ink,#2D364D)" : "var(--card,#fff)",
    color: active && color ? color : active ? "#fff" : "var(--muted,#9C8474)",
    fontFamily: "inherit", fontSize: ".76rem", fontWeight: 700, whiteSpace: "nowrap",
    boxShadow: active && !color ? "0 2px 8px rgba(45,54,77,.1)" : "none",
    ...sx,
  }}>{children}</button>
));

const TabPill = memo(({ active, children, onClick }) => (
  <button className="mf8-btn" onClick={onClick} style={{
    flexShrink: 0,
    padding: "9px 18px", borderRadius: 99, border: "none",
    background: active ? "var(--ink,#2D364D)" : "transparent",
    color: active ? "#fff" : "var(--muted,#9C8474)",
    fontFamily: "inherit", fontSize: ".83rem", fontWeight: 700,
    boxShadow: active ? "0 3px 10px rgba(45,54,77,.13)" : "none",
  }}>{children}</button>
));

/* ═══════════════════════════════════════════════════════════════════════════
   MINI USER STATS BAR
   Shows Post count · Followers · Following from feedUser object
═══════════════════════════════════════════════════════════════════════════ */
const MiniUserStats = memo(({ feedUser, onEdit }) => {
  if (!feedUser) return null;
  const stats = [
    { label: "Posts",     value: feedUser.post_count     ?? 0 },
    { label: "Followers", value: feedUser.follower_count  ?? 0 },
    { label: "Following", value: feedUser.following_count ?? 0 },
  ];
  return (
    <div style={{
      margin: "10px 14px 0",
      background: "var(--card,#fff)",
      borderRadius: 18,
      border: "1px solid var(--sand-light,#EBE7DF)",
      padding: "12px 16px",
      display: "flex", alignItems: "center", gap: 12,
      animation: "mf8SlideUp .4s cubic-bezier(.25,1,.4,1) both",
    }}>
      {/* Avatar */}
      <div style={{
        width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
        background: "var(--brand-wash,rgba(90,105,147,.1))",
        border: "2px solid var(--brand,#5A6993)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 800, fontSize: ".9rem", color: "var(--brand,#5A6993)",
      }}>
        {feedUser.username?.[0]?.toUpperCase()}
      </div>

      {/* Name */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 800, fontSize: ".88rem", color: "var(--ink,#2D364D)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>@{feedUser.username}</div>
        {feedUser.bio && (
          <div style={{
            fontSize: ".7rem", color: "var(--muted,#9C8474)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{feedUser.bio}</div>
        )}
      </div>

      {/* Stats */}
      {stats.map((s, i) => (
        <div key={s.label} style={{
          textAlign: "center", flexShrink: 0,
          borderLeft: i > 0 ? "1px solid var(--sand-light,#EBE7DF)" : "none",
          paddingLeft: i > 0 ? 12 : 0,
        }}>
          <div style={{
            fontFamily: "var(--font-display,'Fraunces',serif)",
            fontSize: "1rem", fontWeight: 700, color: "var(--ink,#2D364D)",
          }}>{s.value}</div>
          <div style={{ fontSize: ".58rem", fontWeight: 700, color: "var(--muted,#9C8474)", textTransform: "uppercase", letterSpacing: ".05em" }}>
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE SPACE CARD
   Fully responsive. join() wired. onOpenSpace() wired. All desktop data shown.
═══════════════════════════════════════════════════════════════════════════ */
const MobileSpaceCard = memo(({ space, currentUser, onOpenSpace, compact = false }) => {
  const [joining, setJoining] = useState(false);
  const [joined,  setJoined]  = useState(false);
  const [err,     setErr]     = useState(null);

  const isMember = joined || (space.user_role && space.user_role !== "none");
  const accent   = space.color || "#6366f1";
  const label    = space.reason === "most_members" ? "🔥 Trending" : "⭐ Featured";

  const handleJoin = useCallback(async (e) => {
    e.stopPropagation();
    if (!currentUser || joining) return;
    haptic("heavy");
    setJoining(true); setErr(null);
    try {
      await joinSpace(space.id, currentUser.id);
      setJoined(true);
    } catch (er) {
      setErr(er.message?.includes("banned") ? "You've been removed." : "Failed. Try again.");
    } finally { setJoining(false); }
  }, [space.id, currentUser, joining]);

  const open = useCallback(() => { haptic("heavy"); onOpenSpace?.(space); }, [space, onOpenSpace]);

  /* ── Compact variant: small pill card for horizontal strip ── */
  if (compact) {
    return (
      <div
        className="mf8-btn"
        onClick={open}
        style={{
          flexShrink: 0, width: 172,
          borderRadius: 18,
          border: `1.5px solid ${accent}28`,
          background: `linear-gradient(135deg,${accent}0c,var(--card,#fff))`,
          padding: "14px 14px 12px",
          position: "relative", overflow: "hidden",
        }}
      >
        <div style={{
          position: "absolute", top: -16, right: -16, width: 60, height: 60,
          borderRadius: "50%", background: `${accent}14`, pointerEvents: "none",
        }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: `${accent}18`, border: `1.5px solid ${accent}28`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
          }}>{space.avatar_emoji || "🏡"}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontWeight: 800, fontSize: ".78rem", color: "var(--ink,#2D364D)",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{space.name}</div>
            <div style={{ fontSize: ".62rem", color: "var(--muted,#9C8474)" }}>
              {space.member_count?.toLocaleString()} members
            </div>
          </div>
        </div>

        {space.weekly_prompt && (
          <p style={{
            margin: "0 0 10px", fontSize: ".7rem", color: "var(--muted,#9C8474)",
            fontStyle: "italic", lineHeight: 1.4,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>"{space.weekly_prompt}"</p>
        )}

        <div style={{ display: "flex", gap: 5 }} onClick={e => e.stopPropagation()}>
          {!isMember ? (
            <button
              className="mf8-btn"
              onClick={handleJoin}
              disabled={joining || !currentUser}
              style={{
                flex: 1, padding: "7px 0", borderRadius: 9, border: "none",
                background: accent, color: "#fff",
                fontFamily: "inherit", fontSize: ".7rem", fontWeight: 700,
                opacity: joining ? .65 : 1,
              }}
            >{joining ? "…" : "Join"}</button>
          ) : (
            <button
              className="mf8-btn"
              onClick={open}
              style={{
                flex: 1, padding: "7px 0", borderRadius: 9, border: "none",
                background: accent, color: "#fff",
                fontFamily: "inherit", fontSize: ".7rem", fontWeight: 700,
              }}
            >View →</button>
          )}
        </div>
        {err && <div style={{ fontSize: ".62rem", color: "#ef4444", marginTop: 5 }}>{err}</div>}
      </div>
    );
  }

  /* ── Full variant: woven into feed ── */
  return (
    <div
      className="mf8-btn"
      onClick={open}
      style={{
        borderRadius: 20,
        border: `1.5px solid ${accent}28`,
        background: `linear-gradient(135deg,${accent}0a,var(--card,#fff))`,
        marginBottom: 10, overflow: "hidden", position: "relative",
      }}
    >
      <div style={{
        position: "absolute", top: -24, right: -24, width: 96, height: 96,
        borderRadius: "50%", background: `${accent}14`, pointerEvents: "none",
      }} />
      <div style={{ padding: "16px 16px 14px", position: "relative" }}>

        {/* Badge + tag */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{
            fontSize: 10, fontWeight: 800, letterSpacing: ".06em",
            textTransform: "uppercase", color: accent,
            background: `${accent}14`, padding: "3px 9px", borderRadius: 99,
          }}>{label}</span>
          {space.tag && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: "var(--muted,#9C8474)",
              background: "var(--sand-light,#EBE7DF)", padding: "3px 9px", borderRadius: 99,
            }}>#{space.tag}</span>
          )}
        </div>

        {/* Identity */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 14, flexShrink: 0,
            background: `${accent}18`, border: `1.5px solid ${accent}28`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24,
          }}>{space.avatar_emoji || "🏡"}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontWeight: 800, fontSize: ".95rem", color: "var(--ink,#2D364D)",
              marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{space.name}</div>
            <div style={{ fontSize: ".72rem", color: "var(--muted,#9C8474)", display: "flex", gap: 8 }}>
              <span>🛡️ @{space.moderator_username}</span>
              <span>👥 {space.member_count?.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Weekly prompt */}
        {space.weekly_prompt && (
          <div style={{
            background: `${accent}0d`, border: `1px solid ${accent}20`,
            borderRadius: 12, padding: "10px 12px", marginBottom: 12,
          }}>
            <div style={{
              fontSize: 9, fontWeight: 800, color: accent,
              textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4,
            }}>💬 This Week's Question</div>
            <p style={{
              margin: 0, fontSize: ".82rem", color: "var(--ink,#2D364D)",
              fontStyle: "italic", lineHeight: 1.5,
              display: "-webkit-box", WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>"{space.weekly_prompt}"</p>
          </div>
        )}

        {/* Top post preview */}
        {space.top_post_content && (
          <div style={{
            background: "var(--card,#fff)", border: `1px solid ${accent}20`,
            borderRadius: 12, padding: "10px 12px", marginBottom: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
              <div style={{
                width: 20, height: 20, borderRadius: "50%",
                background: `${accent}20`, color: accent,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, fontWeight: 900, flexShrink: 0,
              }}>{space.top_post_author?.[0]?.toUpperCase()}</div>
              <span style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--muted,#9C8474)" }}>
                @{space.top_post_author}
              </span>
              {space.top_post_likes_this_week > 0 && (
                <span style={{
                  marginLeft: "auto", fontSize: ".68rem", fontWeight: 700,
                  color: accent, background: `${accent}15`,
                  padding: "1px 7px", borderRadius: 20,
                }}>❤️ {space.top_post_likes_this_week}</span>
              )}
            </div>
            <p style={{
              margin: 0, fontSize: ".8rem", color: "var(--ink,#2D364D)", lineHeight: 1.5,
              display: "-webkit-box", WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>{space.top_post_content}</p>
          </div>
        )}

        {/* Stats */}
        {(space.new_members_this_week > 0 || space.top_post_likes_this_week > 0) && (
          <div style={{ fontSize: ".72rem", color: "var(--muted,#9C8474)", marginBottom: 12, display: "flex", gap: 12 }}>
            {space.new_members_this_week > 0 && <span>🚀 +{space.new_members_this_week} this week</span>}
            {space.top_post_likes_this_week > 0 && <span>❤️ {space.top_post_likes_this_week} on top post</span>}
          </div>
        )}

        {err && <div style={{ fontSize: ".72rem", color: "#ef4444", marginBottom: 8 }}>{err}</div>}

        {/* CTA */}
        <div style={{ display: "flex", gap: 8 }} onClick={e => e.stopPropagation()}>
          {!isMember ? (
            <button
              className="mf8-btn"
              onClick={handleJoin}
              disabled={joining || !currentUser}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 12, border: "none",
                background: accent, color: "#fff",
                fontFamily: "inherit", fontSize: ".83rem", fontWeight: 700,
                opacity: joining ? .65 : 1,
                boxShadow: `0 4px 14px ${accent}30`,
              }}
            >{joining ? "Joining…" : space.weekly_prompt ? "Join & Answer →" : "Join Space →"}</button>
          ) : (
            <button
              className="mf8-btn"
              onClick={open}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 12, border: "none",
                background: accent, color: "#fff",
                fontFamily: "inherit", fontSize: ".83rem", fontWeight: 700,
                boxShadow: `0 4px 14px ${accent}30`,
              }}
            >{space.weekly_prompt ? "Answer →" : "View Space →"}</button>
          )}
          <button
            className="mf8-btn"
            onClick={open}
            style={{
              padding: "10px 16px", borderRadius: 12,
              border: `1.5px solid ${accent}35`,
              background: `${accent}0a`, color: accent,
              fontFamily: "inherit", fontSize: ".83rem", fontWeight: 700,
            }}
          >View</button>
        </div>
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   SPACES BROWSER SHEET
   Full searchable list of all spaces — join / view any space
═══════════════════════════════════════════════════════════════════════════ */
const SpacesBrowserSheet = memo(({ currentUser, onOpenSpace, onClose, onCreateSpace, canCreate }) => {
  const [query,     setQuery]     = useState("");
  const [results,   setResults]   = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    searchSpaces(query.trim() || null, currentUser?.id, 20)
      .then(data => { if (!cancelled) { setResults(data || []); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, currentUser?.id]);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(0,0,0,.52)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", flexDirection: "column", justifyContent: "flex-end",
        animation: "mf8In .18s ease",
      }}
      onClick={onClose}
    >
      <div
        className="mf8-sheet"
        style={{
          background: "var(--bg,#F9F4F0)",
          borderRadius: "26px 26px 0 0",
          maxHeight: "88dvh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 -8px 32px rgba(0,0,0,.12)",
          animation: "mf8Sheet .3s cubic-bezier(.25,1,.4,1)",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle */}
        <div style={{
          width: 36, height: 4, borderRadius: 99,
          background: "var(--sand,#D6D2C4)",
          margin: "12px auto 0", flexShrink: 0,
        }} />

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 18px",
          borderBottom: "0.5px solid var(--sand-light,#EBE7DF)",
          flexShrink: 0,
        }}>
          <span style={{
            fontFamily: "var(--font-display,'Fraunces',serif)",
            fontSize: "1.12rem", fontWeight: 700, color: "var(--ink,#2D364D)",
          }}>🏡 Spaces</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {canCreate && (
              <button
                className="mf8-btn"
                onClick={() => { haptic("heavy"); onCreateSpace(); onClose(); }}
                style={{
                  padding: "7px 14px", borderRadius: 99, border: "none",
                  background: "var(--brand,#5A6993)", color: "#fff",
                  fontFamily: "inherit", fontSize: ".75rem", fontWeight: 700,
                }}
              >+ Create</button>
            )}
            <button
              className="mf8-btn"
              onClick={onClose}
              style={{
                width: 30, height: 30, borderRadius: "50%",
                background: "rgba(118,118,128,.13)", border: "none",
                color: "var(--ink,#2D364D)", fontWeight: 700, fontSize: ".85rem",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >✕</button>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: "10px 14px 6px", flexShrink: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "rgba(118,118,128,.12)", borderRadius: 13, padding: "9px 12px",
          }}>
            <span style={{ fontSize: 15, opacity: .5, flexShrink: 0 }}>🔍</span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search spaces…"
              style={{
                flex: 1, border: "none", background: "transparent", padding: 0,
                color: "var(--ink,#2D364D)", fontFamily: "inherit",
                fontSize: ".88rem", fontWeight: 500, outline: "none",
              }}
            />
            {query && (
              <button
                className="mf8-btn"
                onClick={() => setQuery("")}
                style={{
                  border: "none", padding: 0, flexShrink: 0,
                  background: "rgba(118,118,128,.3)", borderRadius: "50%",
                  width: 17, height: 17,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, color: "#fff", fontWeight: 900,
                }}
              >✕</button>
            )}
          </div>
        </div>

        {/* Spaces list */}
        <div className="mf8-sc" style={{
          flex: 1, overflowY: "auto",
          padding: "6px 14px calc(env(safe-area-inset-bottom,0px) + 16px)",
        }}>
          {loading ? (
            <div style={{ padding: "32px 0", textAlign: "center", color: "var(--muted,#9C8474)", fontSize: ".84rem" }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                border: "3px solid var(--sand-light,#EBE7DF)",
                borderTopColor: "var(--brand,#5A6993)",
                animation: "mf8Spin .85s linear infinite",
                margin: "0 auto 12px",
              }} />
              Loading spaces…
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: "32px 0", textAlign: "center" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>🏡</div>
              <p style={{ fontSize: ".88rem", color: "var(--muted,#9C8474)" }}>
                {query ? "No spaces found" : "No spaces yet"}
              </p>
            </div>
          ) : (
            results.map(space => {
              const accent = space.color || "#6366f1";
              const isMember = space.user_role && space.user_role !== "none";
              return (
                <SpaceBrowserRow
                  key={space.id}
                  space={space}
                  accent={accent}
                  isMember={isMember}
                  currentUser={currentUser}
                  onOpen={() => { onOpenSpace(space); onClose(); }}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
});

/* Single row inside the spaces browser */
const SpaceBrowserRow = memo(({ space, accent, isMember, currentUser, onOpen }) => {
  const [joining, setJoining] = useState(false);
  const [joined,  setJoined]  = useState(false);
  const actualMember = joined || isMember;

  const handleJoin = useCallback(async (e) => {
    e.stopPropagation();
    if (!currentUser || joining) return;
    haptic("heavy");
    setJoining(true);
    try { await joinSpace(space.id, currentUser.id); setJoined(true); }
    catch (_) {}
    finally { setJoining(false); }
  }, [space.id, currentUser, joining]);

  return (
    <div
      className="mf8-btn"
      onClick={onOpen}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "11px 12px", borderRadius: 16, marginBottom: 8,
        border: "1.5px solid var(--sand-light,#EBE7DF)",
        background: "var(--card,#fff)",
      }}
    >
      {/* Emoji */}
      <div style={{
        width: 44, height: 44, borderRadius: 12, flexShrink: 0,
        background: `${accent}18`, border: `1.5px solid ${accent}28`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
      }}>{space.avatar_emoji || "🏡"}</div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 700, fontSize: ".88rem", color: "var(--ink,#2D364D)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>{space.name}</div>
        <div style={{ fontSize: ".7rem", color: "var(--muted,#9C8474)", marginTop: 1 }}>
          👥 {space.member_count?.toLocaleString()} members
          {actualMember && (
            <span style={{ marginLeft: 6, color: accent, fontWeight: 700 }}>
              · {space.user_role === "moderator" || space.user_role === "comoderator" ? "🛡️ Mod" : "✓ Joined"}
            </span>
          )}
        </div>
      </div>

      {/* CTA */}
      <div onClick={e => e.stopPropagation()}>
        {!actualMember ? (
          <button
            className="mf8-btn"
            onClick={handleJoin}
            disabled={joining || !currentUser}
            style={{
              padding: "7px 14px", borderRadius: 99, border: "none",
              background: accent, color: "#fff",
              fontFamily: "inherit", fontSize: ".72rem", fontWeight: 700,
              flexShrink: 0,
            }}
          >{joining ? "…" : "Join"}</button>
        ) : (
          <button
            className="mf8-btn"
            onClick={onOpen}
            style={{
              padding: "7px 14px", borderRadius: 99,
              border: `1.5px solid ${accent}35`,
              background: `${accent}0c`, color: accent,
              fontFamily: "inherit", fontSize: ".72rem", fontWeight: 700,
              flexShrink: 0,
            }}
          >View →</button>
        )}
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   SKELETON + EMPTY STATE
═══════════════════════════════════════════════════════════════════════════ */
function Skel() {
  const line = (w, h = 12, mb = 0) => (
    <div style={{
      width: w, height: h, borderRadius: h,
      background: "var(--sand-light,#EBE7DF)",
      animation: "mf8Pulse 1.7s ease-in-out infinite", marginBottom: mb,
    }} />
  );
  return (
    <div style={{
      background: "var(--card,#fff)", borderRadius: 20, padding: 18,
      border: "1px solid var(--sand-light,#EBE7DF)", marginBottom: 10,
    }}>
      <div style={{ display: "flex", gap: 11, marginBottom: 14, alignItems: "center" }}>
        <div style={{
          width: 42, height: 42, borderRadius: "50%", flexShrink: 0,
          background: "var(--sand-light,#EBE7DF)", animation: "mf8Pulse 1.7s ease-in-out infinite",
        }} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          {line("50%")} {line("32%", 10)}
        </div>
      </div>
      {line("100%", 68, 13)} {line("72%")}
    </div>
  );
}

const EmptyState = memo(({ feedTab, searchQuery }) => {
  const k = searchQuery ? "search" : EMPTY[feedTab] ? feedTab : "default";
  const { icon, title, sub } = EMPTY[k];
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "64px 32px", textAlign: "center",
      animation: "mf8Up .4s cubic-bezier(.25,1,.4,1) both",
    }}>
      <span style={{ fontSize: "3.5rem", display: "block", marginBottom: 18, animation: "mf8Float 4s ease-in-out infinite" }}>
        {icon}
      </span>
      <h3 style={{
        fontFamily: "var(--font-display,'Fraunces',serif)",
        fontSize: "1.45rem", fontWeight: 700, letterSpacing: "-.025em",
        color: "var(--ink,#2D364D)", margin: "0 0 10px",
      }}>{title}</h3>
      <p style={{ fontSize: ".88rem", color: "var(--muted,#9C8474)", lineHeight: 1.65, margin: 0, maxWidth: 250 }}>{sub}</p>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN INNER COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
function MobileFeedInner({ feedUser, needsUsername, registerUsername }) {
  const {
    posts, voteMap, bookmarkMap, reportMap,
    loadingPosts, hasMore, fetchError,
    feedTab, sortBy, activeTag, searchQuery, newPostsBanner,
    setFeedTab, setSortBy, setActiveTag, setSearchQuery,
    loadMore, addPost, removePost, dismissNewPostsBanner,
  } = useFeedContext();

  const { topSpaces } = useTopSpaces();

  /* ── State ───────────────────────────────────────────────────────────── */
  const [openedSpace,     setOpenedSpace]     = useState(null);
  const [showComposer,    setShowComposer]    = useState(false);
  const [showSpacesBrowser, setShowSpacesBrowser] = useState(false);
  const [showCreateSpace, setShowCreateSpace] = useState(false);
  const [viewingProfile,  setViewingProfile]  = useState(null);  // username string
  const [searchDraft,     setSearchDraft]     = useState(searchQuery || "");
  const [headerVisible,   setHeaderVisible]   = useState(true);
  const [headerH,         setHeaderH]         = useState(122);
  const [usernameInput,   setUsernameInput]   = useState(feedUser?.username || "");
  const [regError,        setRegError]        = useState("");
  const [regLoading,      setRegLoading]      = useState(false);

  // Can user create a space?
  const canCreateSpace = feedUser?.is_admin || (feedUser?.follower_count ?? 0) >= 50;

  /* ── Refs ────────────────────────────────────────────────────────────── */
  const scrollRef  = useRef(null);
  const headerDom  = useRef(null);
  const lastY      = useRef(0);
  const ticking    = useRef(false);
  const hdrState   = useRef(true);

  /* Measure header height */
  useEffect(() => {
    if (headerDom.current) setHeaderH(headerDom.current.offsetHeight);
  }, []);

  /* Sync search draft */
  useEffect(() => { setSearchDraft(searchQuery || ""); }, [searchQuery]);

  /* Debounce search → context (380 ms) */
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchDraft.trim()), 380);
    return () => clearTimeout(t);
  }, [searchDraft, setSearchQuery]);

  /* Weave space cards into feed at positions 3 & 8 */
  const postsWithSpaceCards = useMemo(() => {
    if (!topSpaces?.length || !posts.length) return posts;
    const result = [...posts];
    topSpaces.slice(0, 2).forEach((space, i) => {
      const pos = [3, 8][i];
      if (pos !== undefined && pos < result.length)
        result.splice(pos + i, 0, { __isSpaceCard: true, space });
    });
    return result;
  }, [posts, topSpaces]);

  /* RAF-throttled scroll */
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(() => {
      const y = scrollRef.current?.scrollTop ?? 0;
      let next = hdrState.current;
      if      (y > lastY.current + 12 && y > 55) next = false;
      else if (y < lastY.current - 12 || y <= 55) next = true;
      if (next !== hdrState.current) { hdrState.current = next; setHeaderVisible(next); }
      lastY.current = y;
      ticking.current = false;
    });
  }, []);

  /* Stable callbacks */
  const handlePostCreated  = useCallback((p) => {
    haptic("heavy"); addPost(p); setShowComposer(false);
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [addPost]);

  const handleRefresh      = useCallback(() => {
    haptic(); dismissNewPostsBanner();
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [dismissNewPostsBanner]);

  const handleOpenSpace    = useCallback((sp) => { haptic("heavy"); setOpenedSpace(sp); }, []);
  const handleCloseSpace   = useCallback(() => { haptic(); setOpenedSpace(null); }, []);
  const handleSpaceCreated = useCallback((sp) => { setShowCreateSpace(false); setOpenedSpace(sp); }, []);

  const handleRegister = useCallback(async () => {
    if (!usernameInput.trim()) return;
    haptic();
    setRegLoading(true);
    const { error } = await registerUsername(usernameInput.trim());
    if (error) setRegError(error);
    setRegLoading(false);
  }, [usernameInput, registerUsername]);

  /* Memoised post list — stable removePost ref prevents full re-render */
  const renderedPosts = useMemo(() => (
    postsWithSpaceCards.map((item, i) =>
      item.__isSpaceCard ? (
        <MobileSpaceCard
          key={`sc-${item.space.id}`}
          space={item.space}
          currentUser={feedUser}
          onOpenSpace={handleOpenSpace}
        />
      ) : (
        <div key={item.id} style={{
          animation: "mf8Up .42s cubic-bezier(.25,1,.4,1) both",
          animationDelay: `${Math.min(i, 10) * .06}s`,
        }}>
          <PostCard
            post={item}
            currentUser={feedUser}
            onDelete={removePost}
            onNavigateToProfile={setViewingProfile}
            stateProvided={true}
            initialVote={voteMap[item.id] ?? null}
            initialBookmarked={bookmarkMap[item.id] ?? false}
            initialReported={reportMap[item.id] ?? false}
          />
        </div>
      )
    )
  ), [postsWithSpaceCards, feedUser, removePost, voteMap, bookmarkMap, reportMap, handleOpenSpace]);

  /* ══ USERNAME GATE ══════════════════════════════════════════════════ */
  if (needsUsername) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: "56px 28px",
        textAlign: "center", minHeight: "65dvh",
        fontFamily: "var(--font,'Plus Jakarta Sans',sans-serif)",
      }}>
        <GlobalCSS />
        <span style={{ fontSize: "3rem", marginBottom: 18 }}>💬</span>
        <h2 style={{
          fontFamily: "var(--font-display,'Fraunces',serif)",
          fontSize: "1.7rem", fontWeight: 700, letterSpacing: "-.025em",
          color: "var(--ink,#2D364D)", margin: "0 0 10px",
        }}>Join the community</h2>
        <p style={{ fontSize: ".9rem", color: "var(--muted,#9C8474)", lineHeight: 1.65, margin: "0 0 28px" }}>
          Pick a unique username to start sharing.
        </p>
        <input
          value={usernameInput}
          onChange={e => { setUsernameInput(e.target.value); setRegError(""); }}
          placeholder="@username"
          maxLength={20}
          onKeyDown={e => e.key === "Enter" && handleRegister()}
          style={{
            width: "100%", maxWidth: 280, padding: "14px 18px",
            borderRadius: 16,
            border: `2px solid ${regError ? "#dc2626" : "var(--sand-light,#EBE7DF)"}`,
            background: "var(--card,#fff)", color: "var(--ink,#2D364D)",
            fontFamily: "inherit", fontSize: "1rem", textAlign: "center",
            outline: "none", marginBottom: 10, boxSizing: "border-box",
          }}
          onFocus={e => { e.target.style.borderColor = "var(--brand,#5A6993)"; e.target.style.boxShadow = "0 0 0 3px rgba(90,105,147,.12)"; }}
          onBlur={e => { e.target.style.borderColor = regError ? "#dc2626" : "var(--sand-light,#EBE7DF)"; e.target.style.boxShadow = "none"; }}
        />
        {regError && <p style={{ fontSize: ".8rem", color: "#dc2626", fontWeight: 700, margin: "0 0 12px" }}>{regError}</p>}
        <button
          className="mf8-btn"
          onClick={handleRegister}
          disabled={regLoading || !usernameInput.trim()}
          style={{
            width: "100%", maxWidth: 280, padding: "15px",
            background: "var(--brand,#5A6993)", color: "#fff",
            border: "none", borderRadius: 14, fontFamily: "inherit",
            fontSize: ".92rem", fontWeight: 700,
            boxShadow: "0 4px 18px rgba(90,105,147,.28)",
            opacity: (regLoading || !usernameInput.trim()) ? .42 : 1,
          }}
        >{regLoading ? "Creating profile…" : "Enter Community →"}</button>
      </div>
    );
  }

  /* ══ MAIN FEED ══════════════════════════════════════════════════════════
     Flex COLUMN layout:
       [header — absolute]
       [scroll area — flex:1]
       [FAB bar — flex-shrink:0, never scrolls away]
  ════════════════════════════════════════════════════════════════════════ */
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100%", position: "relative",
      background: "var(--bg,#F9F4F0)",
      fontFamily: "var(--font,'Plus Jakarta Sans',sans-serif)",
      overflow: "hidden",
    }}>
      <GlobalCSS />

      {/* ══ AUTO-HIDING GLASS HEADER ══════════════════════════════════════ */}
      <div
        ref={headerDom}
        className="mf8-hdr"
        style={{
          position: "absolute", top: 0, left: 0, right: 0, zIndex: 40,
          background: "rgba(252,250,248,.93)",
          backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
          borderBottom: "0.5px solid rgba(212,207,202,.7)",
          paddingTop: "env(safe-area-inset-top,0px)",
          transition: "transform .26s cubic-bezier(.25,1,.4,1), opacity .26s",
          transform: headerVisible ? "translateY(0)" : "translateY(-110%)",
          opacity: headerVisible ? 1 : 0,
          pointerEvents: headerVisible ? "auto" : "none",
        }}
      >
        {/* Row 1: search + spaces button + compose shortcut */}
        <div style={{ padding: "10px 14px 6px", display: "flex", gap: 8, alignItems: "center" }}>

          {/* iOS inset search */}
          <div style={{
            flex: 1, display: "flex", alignItems: "center", gap: 8,
            background: "rgba(118,118,128,.12)", borderRadius: 13, padding: "9px 12px",
          }}>
            <span style={{ fontSize: 15, opacity: .5, flexShrink: 0 }}>🔍</span>
            <input
              value={searchDraft}
              onChange={e => setSearchDraft(e.target.value)}
              placeholder="Search posts, topics, people…"
              style={{
                flex: 1, border: "none", background: "transparent", padding: 0,
                color: "var(--ink,#2D364D)", fontFamily: "inherit",
                fontSize: ".88rem", fontWeight: 500, outline: "none",
              }}
            />
            {searchDraft && (
              <button
                className="mf8-btn"
                onClick={() => { setSearchDraft(""); setSearchQuery(""); haptic(); }}
                style={{
                  border: "none", padding: 0, flexShrink: 0,
                  background: "rgba(118,118,128,.3)", borderRadius: "50%",
                  width: 17, height: 17,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, color: "#fff", fontWeight: 900,
                }}
              >✕</button>
            )}
          </div>

          {/* 🏡 Spaces browser button */}
          <button
            className="mf8-btn"
            onClick={() => { haptic(); setShowSpacesBrowser(true); }}
            style={{
              width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
              background: "rgba(118,118,128,.12)", border: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.1rem",
            }}
            title="Browse spaces"
          >🏡</button>

          {/* ✏️ Compose shortcut — always reachable */}
          {feedUser && !feedUser.is_banned && (
            <button
              className="mf8-btn"
              onClick={() => { haptic("heavy"); setShowComposer(true); }}
              style={{
                width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                background: "var(--brand,#5A6993)", border: "none", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1rem",
                boxShadow: "0 3px 10px rgba(90,105,147,.3)",
                animation: "mf8FabIn .4s cubic-bezier(.34,1.56,.64,1) .1s both",
              }}
              title="Write post"
            >✏️</button>
          )}
        </div>

        {/* Row 2: feed tab pills */}
        <div className="mf8-sc" style={{
          display: "flex", gap: 5, padding: "3px 14px 11px",
          overflowX: "auto", scrollSnapType: "x mandatory",
        }}>
          {FEED_TABS.map(t => (
            <TabPill key={t.key} active={feedTab === t.key}
              onClick={() => { haptic(); setFeedTab(t.key); }}>
              {t.icon} {t.label}
            </TabPill>
          ))}
        </div>
      </div>

      {/* ══ SCROLLABLE FEED ════════════════════════════════════════════════ */}
      <div
        ref={scrollRef}
        className="mf8-sc"
        onScroll={handleScroll}
        style={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingTop: headerH }}
      >
        {/* New-posts banner */}
        {newPostsBanner && (
          <div
            className="mf8-btn"
            onClick={handleRefresh}
            style={{
              margin: "10px 14px 0", padding: "11px 16px", borderRadius: 14,
              background: "var(--brand-wash,rgba(90,105,147,.08))",
              border: "1px solid var(--brand-light,#A1B0D8)",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              animation: "mf8Bop .38s cubic-bezier(.34,1.56,.64,1) both",
            }}
          >
            <span style={{ fontSize: ".82rem", fontWeight: 700, color: "var(--brand,#5A6993)" }}>
              ✦ New posts available
            </span>
            <span style={{
              padding: "5px 13px", borderRadius: 99,
              background: "var(--brand,#5A6993)", color: "#fff",
              fontSize: ".72rem", fontWeight: 700,
            }}>↑ Refresh</span>
          </div>
        )}

        {/* Mini user stats — always visible */}
        <MiniUserStats feedUser={feedUser} />

        {/* Banned */}
        {feedUser?.is_banned && (
          <div style={{
            margin: "10px 14px 0", padding: "12px 15px", borderRadius: 14,
            background: "rgba(220,38,38,.05)", border: "1px solid rgba(220,38,38,.15)",
            color: "#b91c1c", fontSize: ".83rem", fontWeight: 600,
          }}>🚫 Account suspended. Contact support.</div>
        )}

        {/* Error */}
        {fetchError && (
          <div style={{
            margin: "10px 14px 0", padding: "11px 15px", borderRadius: 14,
            background: "rgba(220,38,38,.05)", border: "1px solid rgba(220,38,38,.15)",
            color: "#b91c1c", fontSize: ".82rem", fontWeight: 600,
          }}>⚠️ {fetchError}</div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            FEATURED SPACES — horizontal scroll strip
            Shows top spaces directly so users can immediately join/view.
            Separate from the feed-woven cards for maximum discoverability.
        ════════════════════════════════════════════════════════════════════ */}
        {topSpaces?.length > 0 && (
          <div style={{ margin: "12px 0 0" }}>
            {/* Section header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "0 14px", marginBottom: 10,
            }}>
              <span style={{
                fontSize: ".68rem", fontWeight: 800, color: "var(--muted,#9C8474)",
                textTransform: "uppercase", letterSpacing: ".07em",
              }}>🏡 Featured Spaces</span>
              <button
                className="mf8-btn"
                onClick={() => { haptic(); setShowSpacesBrowser(true); }}
                style={{
                  fontSize: ".72rem", fontWeight: 700, color: "var(--brand,#5A6993)",
                  background: "none", border: "none", padding: 0, fontFamily: "inherit",
                }}
              >Browse all →</button>
            </div>

            {/* Horizontal scroll of compact space cards */}
            <div className="mf8-sc" style={{
              display: "flex", gap: 10,
              padding: "0 14px 4px",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
            }}>
              {topSpaces.map(space => (
                <MobileSpaceCard
                  key={space.id}
                  space={space}
                  currentUser={feedUser}
                  onOpenSpace={handleOpenSpace}
                  compact
                />
              ))}
              {/* "Browse all" card at end */}
              <div
                className="mf8-btn"
                onClick={() => { haptic(); setShowSpacesBrowser(true); }}
                style={{
                  flexShrink: 0, width: 120,
                  borderRadius: 18,
                  border: "1.5px dashed var(--sand,#D6D2C4)",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  gap: 8, padding: "20px 14px",
                  color: "var(--muted,#9C8474)",
                }}
              >
                <span style={{ fontSize: "1.5rem" }}>🏠</span>
                <span style={{ fontSize: ".72rem", fontWeight: 700, textAlign: "center", lineHeight: 1.3 }}>
                  Browse all spaces
                </span>
              </div>
            </div>
          </div>
        )}

        {/* If no topSpaces yet — show "Browse Spaces" prompt */}
        {(!topSpaces || topSpaces.length === 0) && (
          <div
            className="mf8-btn"
            onClick={() => { haptic(); setShowSpacesBrowser(true); }}
            style={{
              margin: "12px 14px 0", padding: "14px 16px", borderRadius: 16,
              border: "1.5px dashed var(--sand,#D6D2C4)",
              background: "var(--card,#fff)",
              display: "flex", alignItems: "center", gap: 12,
            }}
          >
            <span style={{ fontSize: "1.6rem" }}>🏡</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: ".88rem", color: "var(--ink,#2D364D)" }}>
                Discover Spaces
              </div>
              <div style={{ fontSize: ".74rem", color: "var(--muted,#9C8474)" }}>
                Join communities around topics you care about
              </div>
            </div>
            <span style={{ marginLeft: "auto", color: "var(--muted,#9C8474)", fontSize: "1.1rem" }}>→</span>
          </div>
        )}

        {/* Sort + tag filter strips */}
        <div style={{ padding: "12px 14px 4px", display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Sort */}
          <div className="mf8-sc" style={{
            display: "flex", gap: 7, overflowX: "auto",
            scrollSnapType: "x mandatory", paddingBottom: 2,
          }}>
            {SORT_OPTIONS.map(o => (
              <Chip key={o.value} active={sortBy === o.value}
                onClick={() => { haptic(); setSortBy(o.value); }}>
                {SORT_META[o.value]?.icon} {SORT_META[o.value]?.label}
              </Chip>
            ))}
          </div>

          {/* Tags */}
          <div className="mf8-sc" style={{
            display: "flex", gap: 7, overflowX: "auto",
            scrollSnapType: "x mandatory", paddingBottom: 2,
          }}>
            <Chip active={!activeTag} onClick={() => { haptic(); setActiveTag(null); }}>All Topics</Chip>
            {TAGS.map(t => (
              <Chip key={t.label} active={activeTag === t.label} color={t.color}
                onClick={() => { haptic(); setActiveTag(activeTag === t.label ? null : t.label); }}>
                {t.emoji} {t.label}
              </Chip>
            ))}
          </div>
        </div>

        {/* Posts */}
        {loadingPosts && posts.length === 0 ? (
          <div style={{ padding: "8px 12px 0" }}><Skel /><Skel /><Skel /></div>
        ) : posts.length === 0 ? (
          <EmptyState feedTab={feedTab} searchQuery={searchQuery} />
        ) : (
          <>
            <div className="mf8-posts" style={{ padding: "8px 12px 0" }}>
              {renderedPosts}
            </div>
            {hasMore && (
              <button
                className="mf8-btn"
                onClick={() => { haptic(); loadMore(); }}
                disabled={loadingPosts}
                style={{
                  display: "block",
                  width: "calc(100% - 24px)", margin: "10px 12px 6px",
                  padding: "15px", borderRadius: 14,
                  border: "1.5px dashed var(--sand,#D6D2C4)",
                  background: "transparent", color: "var(--muted,#9C8474)",
                  fontFamily: "inherit", fontSize: ".84rem", fontWeight: 700,
                  textAlign: "center", opacity: loadingPosts ? .5 : 1,
                }}
              >{loadingPosts ? "Loading…" : "Load older posts ↓"}</button>
            )}
            <div style={{ height: 6 }} />
          </>
        )}
      </div>
      {/* ↑ end scroll area */}

      {/* ══ FAB BAR — always pinned, never scrolls away ════════════════════
          Sits in the flex column BELOW the scroll area.
          flex-shrink:0 guarantees it always takes its space.
      ════════════════════════════════════════════════════════════════════ */}
      {feedUser && !feedUser.is_banned && (
        <div style={{
          flexShrink: 0,
          padding: "8px 12px",
          paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 10px)",
        }}>
          <button
            className="mf8-btn mf8-fab"
            onClick={() => { haptic("heavy"); setShowComposer(true); }}
            style={{
              width: "100%",
              display: "flex", alignItems: "center", gap: 11,
              padding: "10px 10px 10px 15px",
              borderRadius: 99,
              border: "0.5px solid rgba(210,205,200,.8)",
              background: "rgba(255,255,255,.95)",
              backdropFilter: "blur(22px)", WebkitBackdropFilter: "blur(22px)",
              boxShadow: "0 4px 18px rgba(45,54,77,.09),0 1px 4px rgba(45,54,77,.06)",
            }}
          >
            <div style={{
              width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
              background: "var(--brand-wash,rgba(90,105,147,.1))",
              border: "2px solid var(--brand,#5A6993)",
              color: "var(--brand,#5A6993)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: ".9rem",
            }}>
              {feedUser?.username?.[0]?.toUpperCase()}
            </div>
            <span style={{
              flex: 1, fontSize: ".88rem", fontWeight: 500,
              color: "var(--muted,#9C8474)", textAlign: "left",
            }}>What's on your mind?</span>
            <div style={{
              width: 42, height: 42, borderRadius: "50%", flexShrink: 0,
              background: "var(--brand,#5A6993)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.3rem", fontWeight: 800,
              boxShadow: "0 3px 10px rgba(90,105,147,.3)",
            }}>＋</div>
          </button>
        </div>
      )}

      {/* ══ COMPOSER BOTTOM SHEET ════════════════════════════════════════ */}
      {showComposer && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 200,
            background: "rgba(0,0,0,.5)",
            backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
            display: "flex", flexDirection: "column", justifyContent: "flex-end",
            animation: "mf8In .18s ease",
          }}
          onClick={() => setShowComposer(false)}
        >
          <div
            className="mf8-sheet"
            style={{
              background: "var(--bg,#F9F4F0)",
              borderRadius: "26px 26px 0 0",
              maxHeight: "91dvh",
              display: "flex", flexDirection: "column",
              boxShadow: "0 -8px 32px rgba(0,0,0,.12)",
              animation: "mf8Sheet .3s cubic-bezier(.25,1,.4,1)",
              overflow: "hidden",
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              width: 36, height: 4, borderRadius: 99,
              background: "var(--sand,#D6D2C4)",
              margin: "12px auto 0", flexShrink: 0,
            }} />
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "13px 18px",
              borderBottom: "0.5px solid var(--sand-light,#EBE7DF)", flexShrink: 0,
            }}>
              <span style={{
                fontFamily: "var(--font-display,'Fraunces',serif)",
                fontSize: "1.12rem", fontWeight: 700, color: "var(--ink,#2D364D)",
              }}>New Post</span>
              <button
                className="mf8-btn"
                onClick={() => { haptic(); setShowComposer(false); }}
                style={{
                  width: 30, height: 30, borderRadius: "50%",
                  background: "rgba(118,118,128,.13)", border: "none",
                  color: "var(--ink,#2D364D)", fontWeight: 700, fontSize: ".85rem",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >✕</button>
            </div>
            <div className="mf8-sc" style={{
              flex: 1, overflowY: "auto",
              padding: "12px 14px",
              paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 16px)",
            }}>
              <PostComposer feedUser={feedUser} onPostCreated={handlePostCreated} />
            </div>
          </div>
        </div>
      )}

      {/* ══ SPACES BROWSER SHEET ═════════════════════════════════════════ */}
      {showSpacesBrowser && (
        <SpacesBrowserSheet
          currentUser={feedUser}
          onOpenSpace={(sp) => { setShowSpacesBrowser(false); handleOpenSpace(sp); }}
          onClose={() => setShowSpacesBrowser(false)}
          onCreateSpace={() => setShowCreateSpace(true)}
          canCreate={canCreateSpace}
        />
      )}

      {/* ══ CREATE SPACE MODAL ════════════════════════════════════════════ */}
      {showCreateSpace && (
        <CreateSpaceModal
          currentUser={feedUser}
          onCreated={handleSpaceCreated}
          onClose={() => setShowCreateSpace(false)}
        />
      )}

      {/* ══ SPACE FEED OVERLAY ═══════════════════════════════════════════ */}
      {openedSpace && (
        <SpaceFeed
          spaceId={openedSpace.id}
          currentUser={feedUser}
          onClose={handleCloseSpace}
        />
      )}

      {/* ══ USER PROFILE PANEL ═══════════════════════════════════════════
          Opened when user taps a username/avatar in any PostCard.
          PostCard calls onNavigateToProfile(username) which sets viewingProfile.
      ════════════════════════════════════════════════════════════════════ */}
      {viewingProfile && (
        <UserProfilePanel
          username={viewingProfile}
          currentUser={feedUser}
          onClose={() => setViewingProfile(null)}
        />
      )}
    </div>
  );
}

/* ─── Root export with FeedProvider ─────────────────────────────────────── */
export default function MobileFeed() {
  const { feedUser, loading, needsUsername, registerUsername } = useFeedUser();

  if (loading) {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: "100%", background: "var(--bg,#F9F4F0)",
      }}>
        <style>{`@keyframes mf8Spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{
          width: 32, height: 32, borderRadius: "50%",
          border: "3px solid var(--sand-light,#EBE7DF)",
          borderTopColor: "var(--brand,#5A6993)",
          animation: "mf8Spin .85s linear infinite",
        }} />
      </div>
    );
  }

  return (
    <FeedProvider currentUser={feedUser}>
      <MobileFeedInner
        feedUser={feedUser}
        needsUsername={needsUsername}
        registerUsername={registerUsername}
      />
    </FeedProvider>
  );
}